import { Parser, Validator, type AssetId } from "@repo/caip";
import {
  PRICE_IMPACT_BPS_MAX,
  PRICE_IMPACT_BPS_MIN,
  SLIPPAGE_BPS_MAX,
  SLIPPAGE_BPS_MIN,
} from "@repo/constants";
import { ACTION_FAMILY } from "@repo/onchain-actions";
import { z } from "zod";
import { RebalanceBatchArgsSchema } from "@repo/evm-onchain-actions/rebalance-batch-contract";
import { ExternalWithdrawalBatchArgsSchema } from "./external-withdrawal";
export { ExternalWithdrawalBatchArgsSchema } from "./external-withdrawal";
export type { ExternalWithdrawalBatchArgs } from "./external-withdrawal";
import { OwnerWithdrawalBatchArgsSchema } from "./owner-withdrawal";
import { lazySchema } from "@repo/lazy-schema";
export { OwnerWithdrawalBatchArgsSchema } from "./owner-withdrawal";
export type { OwnerWithdrawalBatchArgs } from "./owner-withdrawal";

const AmountSchema = lazySchema(() => z.string().regex(/^\d+$/));
const PositiveAmountSchema = lazySchema(() =>
  AmountSchema.refine(
    (value) => {
      // The regex check above may have already failed without aborting the
      // parse, so guard against BigInt throwing on non-integer input.
      try {
        return BigInt(value) > 0n;
      } catch {
        return false;
      }
    },
    {
      message: "amount must be a positive integer string",
    },
  ),
);
const AssetIdSchema = lazySchema(() =>
  z.custom<AssetId>(Validator.isValidAssetId, {
    message: "Invalid assetId",
  }),
);
const AddressSchema = lazySchema(() => z.string().regex(/^0x[a-fA-F0-9]{40}$/));
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const SolanaAddressSchema = lazySchema(() =>
  z.string().regex(/^[1-9A-HJ-NP-Za-km-z]{32,44}$/u),
);
/** SLIP-44 coin type of native SOL (`solana:<genesis>/slip44:501`). */
const SOLANA_NATIVE_SLIP44_REFERENCE = "501";
const HexSchema = lazySchema(() => z.string().regex(/^0x[a-fA-F0-9]+$/));
const PresentObjectSchema = lazySchema(() => z.object({}).passthrough());

const parseChainRef = (chainRef: string) => {
  try {
    return Parser.parseChainId(chainRef);
  } catch {
    return null;
  }
};

const readEvmChainId = (
  parsed: NonNullable<ReturnType<typeof parseChainRef>>,
): number | null => {
  if (
    parsed.chain.namespace !== "eip155" ||
    !/^\d+$/.test(parsed.chain.reference)
  ) {
    return null;
  }

  const chainId = Number(parsed.chain.reference);
  return Number.isSafeInteger(chainId) && chainId > 0 ? chainId : null;
};

/** A Solana mainnet account ref: the Investing Account's Swig wallet, base58 kept. */
const SolanaFundingAccountRefSchema = lazySchema(() =>
  z.string().refine(
    (accountRef) => {
      try {
        const parsed = Parser.parseAccountId(accountRef);
        return (
          parsed.chain.namespace === "solana" &&
          SolanaAddressSchema.safeParse(parsed.account.address).success
        );
      } catch {
        return false;
      }
    },
    { message: "Invalid Solana funding account ref" },
  ),
);
const EvmFundingAccountRefSchema = lazySchema(() =>
  z.string().refine(
    (accountRef) => {
      try {
        const parsed = Parser.parseAccountId(accountRef);
        return (
          readEvmChainId(parsed) !== null &&
          AddressSchema.safeParse(parsed.account.address).success
        );
      } catch {
        return false;
      }
    },
    { message: "funding account must be a valid EVM account reference" },
  ),
);

const canonicalizeParsedChainRef = (
  parsed: NonNullable<ReturnType<typeof parseChainRef>>,
): string => {
  if (parsed.chain.namespace === "eip155") {
    const chainId = readEvmChainId(parsed);
    if (chainId === null) {
      throw new Error("Invalid eip155 chain reference");
    }
    return `eip155:${chainId}`;
  }

  // Non-EVM references can contain case-sensitive base58 identity. Rebuild
  // from parsed components without changing their bytes.
  return `${parsed.chain.namespace}:${parsed.chain.reference}`;
};

export const ExecutionActionScopeSchema = lazySchema(() =>
  z
    .object({
      portfolioId: z.string().min(1),
      chainRef: z.string().min(1).optional(),
      chainId: z.number().int().positive().optional(),
    })
    .superRefine((scope, ctx) => {
      const hasChainRef = scope.chainRef !== undefined;
      const hasChainId = scope.chainId !== undefined;

      if (hasChainRef === hasChainId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "exactly one of chainRef or chainId is required",
          path: [hasChainRef ? "chainRef" : "chainId"],
        });
        return;
      }

      if (scope.chainRef === undefined) {
        return;
      }

      const parsed = parseChainRef(scope.chainRef);
      if (!parsed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "chainRef must be a valid CAIP-2 chain reference",
          path: ["chainRef"],
        });
        return;
      }

      if (parsed.chain.namespace === "eip155") {
        if (readEvmChainId(parsed) === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "eip155 chainRef must contain a safe positive decimal chain id",
            path: ["chainRef"],
          });
        }
      }
    })
    .transform((scope) => {
      if (scope.chainRef === undefined && scope.chainId === undefined) {
        throw new Error(
          "ExecutionActionScopeSchema refinement invariant violated",
        );
      }

      const inputChainRef = scope.chainRef ?? `eip155:${scope.chainId}`;
      const parsed = Parser.parseChainId(inputChainRef);
      const chainRef = canonicalizeParsedChainRef(parsed);
      const chainId =
        parsed.chain.namespace === "eip155"
          ? (readEvmChainId(parsed) ?? undefined)
          : undefined;

      return {
        portfolioId: scope.portfolioId,
        chainRef,
        ...(chainId === undefined ? {} : { chainId }),
      };
    }),
);

const NormalizedExecutionActionScopeSchema = lazySchema(() =>
  z
    .object({
      portfolioId: z.string().min(1),
      chainRef: z.string().min(1),
      chainId: z.number().int().positive().optional(),
    })
    .superRefine((scope, ctx) => {
      const parsed = parseChainRef(scope.chainRef);
      if (!parsed) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "chainRef must be a valid CAIP-2 chain reference",
          path: ["chainRef"],
        });
        return;
      }

      if (parsed.chain.namespace === "eip155") {
        const derivedChainId = readEvmChainId(parsed);
        if (derivedChainId === null || scope.chainId !== derivedChainId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "chainId must match the eip155 chainRef",
            path: ["chainId"],
          });
        }
        return;
      }

      if (scope.chainId !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "chainId is only valid for an eip155 chainRef",
          path: ["chainId"],
        });
      }
    })
    .transform((scope) => {
      const parsed = Parser.parseChainId(scope.chainRef);
      return {
        ...scope,
        chainRef: canonicalizeParsedChainRef(parsed),
      };
    }),
);

/**
 * Cross-service wire form. Raw callers still go through
 * ExecutionActionScopeSchema's exactly-one-of check; this schema additionally
 * accepts the matching chainRef + derived EVM chainId emitted by that parser,
 * so a validated scope remains valid after JSON serialization and reparse.
 */
export const ExecutionActionScopeWireSchema = lazySchema(() =>
  z.union([ExecutionActionScopeSchema, NormalizedExecutionActionScopeSchema]),
);

export const ExecutionActionFailurePolicySchema = lazySchema(() =>
  z.enum(["fail_fast", "continue"]),
);

/**
 * A fee leg carved out of a transfer: `amount` (base units of the same asset)
 * of the transfer's `amount` goes to `to` instead of the transfer's own
 * destination, in the same transaction. The transfer's `amount` stays what
 * leaves the source.
 */
const TransferFeeLegSchema = lazySchema(() =>
  z.object({
    amount: PositiveAmountSchema,
    to: z.string().min(1),
  }),
);

/** A fee leg must leave the transfer's own destination something to receive. */
const refineTransferFeeLeg = (
  transfer: {
    readonly amount: string;
    readonly fee?: { readonly amount: string } | undefined;
  },
  ctx: z.RefinementCtx,
): void => {
  if (!transfer.fee || !/^\d+$/u.test(transfer.amount)) return;
  if (BigInt(transfer.fee.amount) >= BigInt(transfer.amount)) {
    ctx.addIssue({
      code: "custom",
      message: "fee amount must be less than the transfer amount",
      path: ["fee", "amount"],
    });
  }
};

const ExternalTransferArgsSchema = lazySchema(() =>
  z
    .object({
      assetId: AssetIdSchema,
      amount: AmountSchema,
      to: z.string().min(1),
      fee: TransferFeeLegSchema.optional(),
    })
    .superRefine(refineTransferFeeLeg),
);

/**
 * A transfer to another portfolio carries no fee leg: a fee pays an address
 * out of a withdrawal. Named here so the union refuses one instead of
 * stripping it as an unknown key, which would move the whole amount to the
 * portfolio although admission required withdraw authority for the leg.
 */
const PortfolioTransferArgsSchema = lazySchema(() =>
  z.object({
    assetId: AssetIdSchema,
    amount: AmountSchema,
    toPortfolioId: z.string().trim().min(1),
    fee: z.undefined(),
  }),
);

export const ExecutionTransferArgsSchema = lazySchema(() =>
  z.union([ExternalTransferArgsSchema, PortfolioTransferArgsSchema]),
);

const slippageBpsSchema = lazySchema(() =>
  z.number().int().min(SLIPPAGE_BPS_MIN).max(SLIPPAGE_BPS_MAX).optional(),
);
const maxPriceImpactBpsSchema = lazySchema(() =>
  z
    .number()
    .int()
    .min(PRICE_IMPACT_BPS_MIN)
    .max(PRICE_IMPACT_BPS_MAX)
    .optional(),
);
const feePercentSchema = lazySchema(() =>
  z.number().finite().min(0).max(1).nullable().optional(),
);
const executionProviderSchema = lazySchema(() =>
  z.enum(["lifi", "relay", "bebop_rfq", "bebop_jam", "ondo_direct"]).optional(),
);
const bebopRfqExpiryTypeSchema = lazySchema(() =>
  z.enum(["standard", "short"]).optional(),
);
const bebopRfqFeeBpsSchema = lazySchema(() =>
  z.number().int().min(0).max(500).optional(),
);
const PreTradeAccountingGuardWatermarkValueSchema = lazySchema(() =>
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);

export const ExecutionPreTradeAccountingGuardSchema = lazySchema(() =>
  z.object({
    kind: z.literal("indexer_v4_accounting_pnl"),
    guardSource: z.literal("matcher_trigger"),
    fromAssetId: AssetIdSchema,
    assetId: z.string().trim().min(1),
    exposureId: z.string().trim().min(1),
    signalSnapshotId: z.string().trim().min(1),
    accountingStreamVersion: z.string().trim().min(1),
    sourceFinality: z.enum([
      "observed",
      "sequenced",
      "safe",
      "finalized",
      "orphaned",
    ]),
    status: z.enum([
      "ready_finalized",
      "ready_provisional",
      "partial",
      "unavailable",
    ]),
    actionable: z.boolean(),
    basisCompletenessRatio: z.string().trim().min(1),
    valuationCompletenessRatio: z.string().trim().min(1),
    unknownBasisQuantityBaseUnits: AmountSchema,
    metric: z.enum(["cost_basis_multiple", "unrealized_pnl_ratio"]),
    threshold: z.string().trim().min(1),
    observedValue: z.string().trim().min(1),
    allowProvisional: z.boolean().optional(),
    sourceEventCount: z.number().int().nonnegative(),
    sourceEventWatermark: z.record(
      z.string(),
      PreTradeAccountingGuardWatermarkValueSchema,
    ),
  }),
);

const ExecutionSwapBaseArgsSchema = lazySchema(() =>
  z.object({
    appFee: z.never().optional(),
    executionProvider: executionProviderSchema,
    expiryType: bebopRfqExpiryTypeSchema,
    feeBps: bebopRfqFeeBpsSchema,
    fromAssetId: AssetIdSchema,
    toAssetId: AssetIdSchema,
    amount: AmountSchema,
    slippageBps: slippageBpsSchema,
    maxPriceImpactBps: maxPriceImpactBpsSchema,
    feePercent: feePercentSchema,
    preTradeAccountingGuard: ExecutionPreTradeAccountingGuardSchema.optional(),
  }),
);

const ExecutionSwapExternalDestinationArgsSchema = lazySchema(() =>
  ExecutionSwapBaseArgsSchema.extend({
    to: AddressSchema,
  }),
);

const ExecutionSwapPortfolioDestinationArgsSchema = lazySchema(() =>
  ExecutionSwapBaseArgsSchema.extend({
    toPortfolioId: z.string().trim().min(1),
  }),
);

const ExecutionOndoDirectBatchLegAssetSchema = lazySchema(() =>
  z.object({
    assetId: AssetIdSchema,
    assetTokenAddress: AddressSchema.optional(),
  }),
);
const ExecutionOndoDirectBatchSellLegSchema = lazySchema(() =>
  ExecutionOndoDirectBatchLegAssetSchema.extend({
    amount: PositiveAmountSchema,
    // The planner deliberately allows a 0 minimum.
    minCashOutRaw: AmountSchema.optional(),
  }),
);
const ExecutionOndoDirectBatchBuyLegSchema = lazySchema(() =>
  ExecutionOndoDirectBatchLegAssetSchema.extend({
    cashAmount: PositiveAmountSchema,
    // The planner deliberately allows a 0 minimum.
    minTokenOutRaw: AmountSchema.optional(),
  }),
);
const ExecutionOndoDirectBatchBaseArgsSchema = lazySchema(() =>
  z.object({
    quoteMode: z.literal("fetch"),
    marketHoursPolicy: z.enum(["hold", "reject"]).optional(),
    cashAssetId: AssetIdSchema,
    cashTokenAddress: AddressSchema,
    cashInputAmount: PositiveAmountSchema.optional(),
    feePolicy: z
      .object({
        feeBps: z.number().int().min(0).max(100),
        feeBasis: z.enum([
          "none",
          "buy_notional",
          "sell_notional",
          "single_side_turnover",
          "gross_leg_notional",
          "net_cash_flow",
        ]),
        recipient: AddressSchema.refine((value) => !/^0x0{40}$/i.test(value)),
      })
      .strict()
      .optional(),
    sells: z.array(ExecutionOndoDirectBatchSellLegSchema).max(50).optional(),
    buys: z.array(ExecutionOndoDirectBatchBuyLegSchema).max(50).optional(),
    residualCashPolicy: z.literal("sweep_to_user").optional(),
    approvalMode: z
      .enum(["require_existing", "submit_missing_approvals"])
      .optional(),
  }),
);

type ExecutionOndoDirectBatchBaseArgs = z.infer<
  typeof ExecutionOndoDirectBatchBaseArgsSchema
>;

const EIP155_ASSET_CHAIN_PATTERN = /^eip155:(\d+)[:/]/;

const parseEip155AssetChainId = (assetId: string): number | null => {
  const match = EIP155_ASSET_CHAIN_PATTERN.exec(assetId);
  if (!match?.[1]) {
    return null;
  }
  const chainId = Number(match[1]);
  return Number.isSafeInteger(chainId) ? chainId : null;
};

const isEip155Erc20AssetOnChain = (
  assetId: string,
  chainId: number,
): boolean => {
  try {
    const parsed = Parser.parseAssetId(assetId);
    return (
      parsed.chain.namespace === "eip155" &&
      parsed.chain.reference === String(chainId) &&
      parsed.asset.namespace === "erc20"
    );
  } catch {
    return false;
  }
};

const listOndoDirectBatchLegs = (args: ExecutionOndoDirectBatchBaseArgs) => [
  ...(args.sells ?? []).map((leg, index) => ({
    leg,
    side: "sells" as const,
    index,
  })),
  ...(args.buys ?? []).map((leg, index) => ({
    leg,
    side: "buys" as const,
    index,
  })),
];

const refineOndoDirectBatchArgs = (
  args: ExecutionOndoDirectBatchBaseArgs,
  ctx: z.RefinementCtx,
): void => {
  const legs = listOndoDirectBatchLegs(args);
  if (legs.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "at least one Ondo Direct batch leg is required",
    });
    return;
  }

  const cashAssetKey = args.cashAssetId.toLowerCase();
  const cashTokenKey = args.cashTokenAddress.toLowerCase();
  const cashChainId = parseEip155AssetChainId(args.cashAssetId);
  const seenAssetIds = new Set<string>();
  const seenTokenAddresses = new Set<string>();

  for (const { leg, side, index } of legs) {
    const assetKey = leg.assetId.toLowerCase();
    if (assetKey === cashAssetKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Ondo Direct batch leg assetId must differ from cashAssetId",
        path: [side, index, "assetId"],
      });
    }
    if (seenAssetIds.has(assetKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "duplicate assetId across Ondo Direct batch legs",
        path: [side, index, "assetId"],
      });
    }
    seenAssetIds.add(assetKey);

    const legChainId = parseEip155AssetChainId(leg.assetId);
    if (
      cashChainId !== null &&
      legChainId !== null &&
      legChainId !== cashChainId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Ondo Direct batch leg assetId must reference the same chain as cashAssetId",
        path: [side, index, "assetId"],
      });
    }

    if (leg.assetTokenAddress !== undefined) {
      const tokenKey = leg.assetTokenAddress.toLowerCase();
      if (tokenKey === cashTokenKey) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Ondo Direct batch leg assetTokenAddress must differ from cashTokenAddress",
          path: [side, index, "assetTokenAddress"],
        });
      }
      if (seenTokenAddresses.has(tokenKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "duplicate assetTokenAddress across Ondo Direct batch legs",
          path: [side, index, "assetTokenAddress"],
        });
      }
      seenTokenAddresses.add(tokenKey);
    }
  }
};

const ExecutionOndoDirectBatchArgsSchema = lazySchema(() =>
  ExecutionOndoDirectBatchBaseArgsSchema.extend({
    executionProvider: z.literal("ondo_direct"),
  }).superRefine(refineOndoDirectBatchArgs),
);

const ExecutionOndoDirectBatchRebalanceArgsSchema = lazySchema(() =>
  ExecutionOndoDirectBatchBaseArgsSchema.superRefine(refineOndoDirectBatchArgs),
);

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasDefinedKey = (value: Record<string, unknown>, key: string): boolean =>
  key in value && value[key] !== undefined;

/**
 * True when a swap payload presents Ondo Direct batch legs (`sells`/`buys`
 * keys), regardless of the declared execution provider. Such payloads must
 * parse as a valid batch — falling through to the legacy single-swap schemas
 * (which strip unknown keys) would silently downgrade a batch intent into a
 * single swap.
 */
const presentsOndoDirectBatchLegs = (value: unknown): boolean =>
  isPlainRecord(value) &&
  (hasDefinedKey(value, "sells") || hasDefinedKey(value, "buys"));

export const ExecutionSwapArgsSchema = lazySchema(() =>
  z
    .unknown()
    .superRefine((value, ctx) => {
      if (isPlainRecord(value) && hasDefinedKey(value, "appFee")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "app fees require an explicit supported Relay action",
        });
      }
      if (
        isPlainRecord(value) &&
        value.executionProvider === "relay" &&
        ((hasDefinedKey(value, "quoteMode") && value.quoteMode !== "fetch") ||
          hasDefinedKey(value, "route"))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Relay swap args only support fetched quotes",
        });
      }
      if (!presentsOndoDirectBatchLegs(value)) {
        return;
      }
      const batchResult = ExecutionOndoDirectBatchArgsSchema.safeParse(value);
      if (batchResult.success) {
        return;
      }
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "swap args present batch legs (sells/buys) but do not match the Ondo Direct batch schema; refusing to downgrade to a single swap",
      });
      for (const issue of batchResult.error.issues) {
        ctx.addIssue({ ...issue });
      }
    })
    .pipe(
      z.union([
        z.object({
          quoteMode: z.literal("provided"),
          executionProvider: z.literal("lifi").optional(),
          route: PresentObjectSchema,
          slippageBps: slippageBpsSchema,
        }),
        ExecutionOndoDirectBatchArgsSchema,
        ExecutionSwapExternalDestinationArgsSchema,
        ExecutionSwapPortfolioDestinationArgsSchema,
        ExecutionSwapBaseArgsSchema,
      ]),
    ),
);

const ExecutionLifiSwapProvidedArgsSchema = lazySchema(() =>
  z.object({
    quoteMode: z.literal("provided"),
    route: PresentObjectSchema,
    slippageBps: slippageBpsSchema,
    nonceScopeId: z.string().min(1).optional(),
    strategyInstanceId: z.string().min(1).optional(),
    stepIndex: z.number().int().nonnegative().optional(),
  }),
);

const ExecutionLifiSwapFetchArgsSchema = lazySchema(() =>
  z.object({
    quoteMode: z.literal("fetch"),
    fromTokenAddress: AddressSchema,
    toTokenAddress: AddressSchema,
    amount: AmountSchema,
    slippageBps: slippageBpsSchema,
    maxPriceImpactBps: maxPriceImpactBpsSchema,
    feePercent: feePercentSchema,
    routeOptions: z
      .object({
        exchanges: z
          .object({
            allow: z.array(z.string().trim().min(1)).min(1).optional(),
            deny: z.array(z.string().trim().min(1)).min(1).optional(),
            prefer: z.array(z.string().trim().min(1)).min(1).optional(),
          })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
    to: AddressSchema.optional(),
    toPortfolioId: z.string().trim().min(1).optional(),
    nonceScopeId: z.string().min(1).optional(),
    strategyInstanceId: z.string().min(1).optional(),
    stepIndex: z.number().int().nonnegative().optional(),
    preTradeAccountingGuard: ExecutionPreTradeAccountingGuardSchema.optional(),
  }),
);

export const ExecutionLifiSwapArgsSchema = lazySchema(() =>
  z.union([
    ExecutionLifiSwapProvidedArgsSchema,
    ExecutionLifiSwapFetchArgsSchema,
  ]),
);

export const ExecutionRelayAppFeePolicySchema = lazySchema(() =>
  z
    .object({
      recipient: AddressSchema.refine(
        (value) => value.toLowerCase() !== ZERO_ADDRESS,
        {
          message: "app fee recipient must be nonzero",
        },
      ).transform((value) => value.toLowerCase()),
      feeBps: z.number().int().min(1).max(500),
    })
    .strict(),
);

export const ExecutionRelaySwapArgsSchema = lazySchema(() =>
  z.object({
    quoteMode: z.literal("fetch").default("fetch"),
    route: z.never().optional(),
    tradeType: z.never().optional(),
    fromTokenAddress: AddressSchema,
    toTokenAddress: AddressSchema,
    amount: AmountSchema,
    slippageBps: slippageBpsSchema,
    maxPriceImpactBps: maxPriceImpactBpsSchema,
    appFee: ExecutionRelayAppFeePolicySchema.optional(),
    appFees: z.never().optional(),
    feeBps: z.never().optional(),
    feePercent: z.never().optional(),
    to: AddressSchema.optional(),
    toPortfolioId: z.string().trim().min(1).optional(),
    preTradeAccountingGuard: ExecutionPreTradeAccountingGuardSchema.optional(),
    fundingSourceAccountRef: EvmFundingAccountRefSchema.optional(),
  }),
);

export const ExecutionRelayBridgeArgsSchema = lazySchema(() =>
  z.object({
    quoteMode: z.never().optional(),
    route: z.never().optional(),
    assetId: AssetIdSchema,
    amount: AmountSchema,
    destinationChainId: z.number().int().positive(),
    destinationAssetId: AssetIdSchema,
    slippageBps: slippageBpsSchema,
    appFee: ExecutionRelayAppFeePolicySchema.optional(),
    appFees: z.never().optional(),
    feeBps: z.never().optional(),
    feePercent: z.never().optional(),
    expectedAmountMinRaw: z
      .string()
      .regex(/^[1-9]\d*$/)
      .optional(),
    bridgeIntentPurpose: z.literal("fund_leg").optional(),
    fundingSourceAccountRef: EvmFundingAccountRefSchema.optional(),
    fundingDestinationAccountRef: EvmFundingAccountRefSchema.optional(),
  }),
);

export const ExecutionBridgeArgsSchema = lazySchema(() =>
  z.object({
    appFee: z.never().optional(),
    fromAssetId: AssetIdSchema,
    toAssetId: AssetIdSchema,
    amount: AmountSchema,
    slippageBps: slippageBpsSchema,
  }),
);

const ExecutionYieldActionArgsSchema = lazySchema(() =>
  z.object({
    assetId: AssetIdSchema,
    amount: AmountSchema,
    protocolId: z.string().min(1),
  }),
);

export const ExecutionYieldWithdrawArgsSchema = ExecutionYieldActionArgsSchema;

export const ExecutionYieldDepositArgsSchema = ExecutionYieldActionArgsSchema;

export const ExecutionYieldRedeemArgsSchema = lazySchema(() =>
  z.object({
    shareAssetId: AssetIdSchema,
    amount: AmountSchema,
    protocolId: z.string().min(1),
    useMaxBalance: z.boolean().optional(),
  }),
);

export const ExecutionCallArgsSchema = lazySchema(() =>
  z.object({
    to: AddressSchema,
    data: HexSchema,
    value: z.string().min(1).optional(),
  }),
);

const createCategoryActionInputSchema = <
  TType extends string,
  TArgsSchema extends z.ZodTypeAny,
>(params: {
  type: TType;
  args: TArgsSchema;
}) =>
  z.object({
    type: z.literal(params.type),
    scope: ExecutionActionScopeSchema,
    failurePolicy: ExecutionActionFailurePolicySchema.optional(),
    args: params.args,
  });

const createExplicitActionInputSchema = <
  TType extends string,
  TArgsSchema extends z.ZodTypeAny,
>(params: {
  type: TType;
  args: TArgsSchema;
}) =>
  z.object({
    type: z.literal(params.type),
    scope: ExecutionActionScopeSchema,
    failurePolicy: ExecutionActionFailurePolicySchema.optional(),
    args: params.args,
  });

const TransferActionInputSchema = lazySchema(() =>
  createCategoryActionInputSchema({
    type: ACTION_FAMILY.transfer,
    args: ExecutionTransferArgsSchema,
  }),
);
const YieldWithdrawActionInputSchema = lazySchema(() =>
  createCategoryActionInputSchema({
    type: ACTION_FAMILY.yieldWithdraw,
    args: ExecutionYieldWithdrawArgsSchema,
  }),
);
const YieldRedeemActionInputSchema = lazySchema(() =>
  createCategoryActionInputSchema({
    type: ACTION_FAMILY.yieldRedeem,
    args: ExecutionYieldRedeemArgsSchema,
  }),
);
const SwapActionInputSchema = lazySchema(() =>
  createCategoryActionInputSchema({
    type: ACTION_FAMILY.swap,
    args: ExecutionSwapArgsSchema,
  }),
);
const BridgeActionInputSchema = lazySchema(() =>
  createCategoryActionInputSchema({
    type: ACTION_FAMILY.bridge,
    args: ExecutionBridgeArgsSchema,
  }),
);
const YieldDepositActionInputSchema = lazySchema(() =>
  createCategoryActionInputSchema({
    type: ACTION_FAMILY.yieldDeposit,
    args: ExecutionYieldDepositArgsSchema,
  }),
);
const CallActionInputSchema = lazySchema(() =>
  createCategoryActionInputSchema({
    type: ACTION_FAMILY.call,
    args: ExecutionCallArgsSchema,
  }),
);

/** An EVM token transfer; an optional fee leg pays part of the amount to an
 * EVM fee wallet in the same userOp. */
const Erc20TransferActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "erc20_transfer",
    args: z
      .object({
        tokenAddress: AddressSchema,
        recipient: AddressSchema,
        amount: AmountSchema,
        fee: z
          .object({ amount: PositiveAmountSchema, to: AddressSchema })
          .optional(),
      })
      .superRefine(refineTransferFeeLeg),
  }),
);
const OwnerWithdrawalBatchActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "owner_withdrawal_batch",
    args: OwnerWithdrawalBatchArgsSchema,
  }),
);
const ExternalWithdrawalBatchActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "external_withdrawal_batch",
    args: ExternalWithdrawalBatchArgsSchema,
  }),
);
const FloatTransferActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "float_transfer",
    args: z.object({
      attemptIndex: z.number().int().nonnegative(),
      assetId: AssetIdSchema,
      amount: PositiveAmountSchema,
      expectedAmountMinRaw: PositiveAmountSchema,
      fundingSourceAccountRef: z.string().trim().min(1),
      fundingDestinationAccountRef: z.string().trim().min(1),
    }),
  }),
);
/**
 * The Solana vault transfer: an SPL token or native SOL (`slip44:501`) to a
 * raw Solana owner address, or to another portfolio's Solana vault, which
 * the executor resolves from `toPortfolioId`. An optional fee leg pays part
 * of the amount to a Solana fee wallet in the same transaction.
 */
const SplTransferActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "spl_transfer",
    // One object, not a union: a union would match the first branch and
    // strip the other destination, silently choosing between two.
    args: z
      .object({
        assetId: AssetIdSchema,
        amount: AmountSchema,
        to: SolanaAddressSchema.optional(),
        toPortfolioId: z.string().trim().min(1).optional(),
        fee: z
          .object({ amount: PositiveAmountSchema, to: SolanaAddressSchema })
          .optional(),
      })
      .superRefine(refineTransferFeeLeg)
      .transform(({ to, toPortfolioId, ...transfer }, ctx) => {
        if (to !== undefined && toPortfolioId === undefined) {
          return { ...transfer, to };
        }
        if (toPortfolioId !== undefined && to === undefined) {
          return { ...transfer, toPortfolioId };
        }
        ctx.addIssue({
          code: "custom",
          message: "spl_transfer needs exactly one of to or toPortfolioId",
          path: ["toPortfolioId"],
        });
        return z.NEVER;
      }),
  }),
);
const SolanaDeliverProceedsActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "solana_deliver_proceeds",
    args: z
      .object({
        executionAccountRef: z.string().min(1),
        assetId: AssetIdSchema,
        recipient: SolanaAddressSchema,
        sources: z
          .array(
            z.object({ actionIndex: z.number().int().nonnegative() }).strict(),
          )
          .min(1)
          .refine(
            (sources) =>
              new Set(sources.map((source) => source.actionIndex)).size ===
              sources.length,
            "Duplicate proceeds source",
          ),
      })
      .strict(),
  }),
);
const SolanaSwapArgsSchema = lazySchema(() =>
  z.object({
    inputAssetId: AssetIdSchema,
    outputAssetId: AssetIdSchema,
    amountRaw: PositiveAmountSchema,
    slippageBps: z.number().int().min(0).max(10_000),
    minOutRaw: PositiveAmountSchema,
    maxPriceImpactBps: z.number().int().min(0).max(10_000).optional(),
    platformFeeBps: z.number().int().min(0).max(10_000).optional(),
    feeAccount: SolanaAddressSchema.optional(),
  }),
);
const SolanaSwapActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "solana_swap",
    args: SolanaSwapArgsSchema,
  }),
);
/**
 * Same args as `solana_swap`; the type pins the venue. A leg touching an Ondo
 * Global Markets mint settles through the GM program (USDC-bridged when
 * neither side is USDC) and never through an open aggregator.
 */
const SolanaOndoSwapActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "solana_ondo_swap",
    args: SolanaSwapArgsSchema,
  }),
);
/**
 * The Investing Account's Solana vault deposits its SPL cash into Relay's
 * depository for a fill on an EVM chain (an EVM buy funded from Solana cash).
 * Mirrors `relay_bridge`: the source asset sits on the action's own Solana
 * chain, the destination asset on `destinationChainId`; the funding refs name
 * the signed source and destination accounts.
 */
const SolanaBridgeActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "solana_bridge",
    args: z.object({
      assetId: AssetIdSchema,
      amount: PositiveAmountSchema,
      destinationChainId: z.number().int().positive(),
      destinationAssetId: AssetIdSchema,
      slippageBps: slippageBpsSchema,
      expectedAmountMinRaw: PositiveAmountSchema.optional(),
      bridgeIntentPurpose: z.literal("fund_leg").optional(),
      fundingSourceAccountRef: SolanaFundingAccountRefSchema.optional(),
      fundingDestinationAccountRef: EvmFundingAccountRefSchema.optional(),
    }),
  }),
);
const addSolanaBridgeScopeIssues = (
  action: z.infer<typeof SolanaBridgeActionInputSchema>,
  ctx: z.RefinementCtx,
): void => {
  const source = Parser.parseAssetId(action.args.assetId);
  if (
    source.chain.namespace !== "solana" ||
    `${source.chain.namespace}:${source.chain.reference}` !==
      action.scope.chainRef ||
    source.asset.namespace !== "spl" ||
    !SolanaAddressSchema.safeParse(source.asset.reference).success
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "solana_bridge assetId must be an SPL mint on scope.chainRef",
      path: ["args", "assetId"],
    });
  }
  const destination = Parser.parseAssetId(action.args.destinationAssetId);
  if (
    readEvmChainId(destination) !== action.args.destinationChainId ||
    destination.asset.namespace !== "erc20" ||
    !AddressSchema.safeParse(destination.asset.reference).success
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        "solana_bridge destinationAssetId must be an ERC-20 on destinationChainId",
      path: ["args", "destinationAssetId"],
    });
  }
  if (
    action.args.fundingSourceAccountRef !== undefined &&
    !action.args.fundingSourceAccountRef.startsWith(`${action.scope.chainRef}:`)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "solana_bridge funding source must be on scope.chainRef",
      path: ["args", "fundingSourceAccountRef"],
    });
  }
};
/**
 * Both swap sides must be Solana assets on the action's own chain: an SPL
 * mint (base58, case preserved) or native SOL as `slip44:501`. Identical sides
 * are rejected before any venue is consulted.
 */
const addSolanaSwapScopeIssues = (
  action:
    | z.infer<typeof SolanaSwapActionInputSchema>
    | z.infer<typeof SolanaOndoSwapActionInputSchema>,
  ctx: z.RefinementCtx,
): void => {
  const isSolanaAssetOnScope = (assetId: string): boolean => {
    const asset = Parser.parseAssetId(assetId);
    const assetChainRef = `${asset.chain.namespace}:${asset.chain.reference}`;
    if (
      asset.chain.namespace !== "solana" ||
      assetChainRef !== action.scope.chainRef
    ) {
      return false;
    }
    if (asset.asset.namespace === "spl") {
      return SolanaAddressSchema.safeParse(asset.asset.reference).success;
    }
    return (
      asset.asset.namespace === "slip44" && asset.asset.reference === "501"
    );
  };
  for (const field of ["inputAssetId", "outputAssetId"] as const) {
    if (!isSolanaAssetOnScope(action.args[field])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${action.type} ${field} must be a Solana SPL or native asset on scope.chainRef`,
        path: ["args", field],
      });
    }
  }
  if (action.args.inputAssetId === action.args.outputAssetId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${action.type} input and output assets must differ`,
      path: ["args", "outputAssetId"],
    });
  }
};
/** Realized proceeds to the recipient; an optional fee leg pays a fixed
 * part of them to an EVM fee wallet in the same userOp. */
const DeliverProceedsActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "deliver_proceeds",
    args: z.object({
      recipient: AddressSchema,
      tokenAddress: AddressSchema,
      sources: z
        .array(
          z.object({
            actionIndex: z.number().int().nonnegative(),
          }),
        )
        .min(1),
      partialDelivery: z.enum(["deliver", "withhold"]),
      fee: z
        .object({ amount: PositiveAmountSchema, to: AddressSchema })
        .optional(),
    }),
  }),
);
/** A native-asset transfer; an optional fee leg pays part of the amount to
 * an EVM fee wallet in the same userOp. */
const NativeTransferActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "native_transfer",
    args: z
      .object({
        recipient: AddressSchema,
        amount: AmountSchema,
        fee: z
          .object({ amount: PositiveAmountSchema, to: AddressSchema })
          .optional(),
      })
      .superRefine(refineTransferFeeLeg),
  }),
);
const BebopRfqSwapActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "bebop_rfq_swap",
    args: z.object({
      quoteMode: z.literal("fetch"),
      fromTokenAddress: AddressSchema,
      toTokenAddress: AddressSchema,
      amount: AmountSchema,
      expiryType: bebopRfqExpiryTypeSchema,
      feeBps: bebopRfqFeeBpsSchema,
      to: AddressSchema.optional(),
      toPortfolioId: z.string().trim().min(1).optional(),
    }),
  }),
);
const BebopJamSwapActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "bebop_jam_swap",
    args: z.object({
      quoteMode: z.literal("fetch"),
      fromTokenAddress: AddressSchema,
      toTokenAddress: AddressSchema,
      amount: AmountSchema,
      slippageBps: slippageBpsSchema,
      feeBps: bebopRfqFeeBpsSchema,
      to: AddressSchema.optional(),
      toPortfolioId: z.string().trim().min(1).optional(),
    }),
  }),
);
const OndoDirectMintRedeemActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "ondo_direct_mint_redeem",
    args: z.object({
      quoteMode: z.literal("fetch"),
      side: z.enum(["buy", "sell"]),
      sellAssetId: AssetIdSchema,
      buyAssetId: AssetIdSchema,
      sellAmount: AmountSchema,
      cashTokenAddress: AddressSchema,
      assetTokenAddress: AddressSchema,
      marketHoursPolicy: z.enum(["hold", "reject"]).optional(),
      to: AddressSchema.optional(),
      toPortfolioId: z.string().trim().min(1).optional(),
    }),
  }),
);
const OndoDirectBatchRebalanceActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "ondo_direct_batch_rebalance",
    args: ExecutionOndoDirectBatchRebalanceArgsSchema,
  }),
);
const RealityViaBitgetSwapActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "reality_via_bitget_swap",
    args: z
      .object({
        quoteMode: z.literal("fetch"),
        sellAssetId: AssetIdSchema,
        buyAssetId: AssetIdSchema,
        sellAmount: PositiveAmountSchema,
        realityAssetId: AssetIdSchema,
        realitySide: z.enum(["buy", "sell"]),
        integrationMode: z.literal("instruction"),
        recipient: z.literal("vault"),
        feePolicyRef: z.literal("bitget-default-v1"),
        slippageBps: z.literal(100),
      })
      .superRefine((args, ctx) => {
        const expectedRealityAssetId =
          args.realitySide === "buy" ? args.buyAssetId : args.sellAssetId;
        if (args.realityAssetId !== expectedRealityAssetId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "realityAssetId must identify the Reality leg selected by realitySide",
            path: ["realityAssetId"],
          });
        }
      }),
  }),
);
const LifiSwapActionInputSchema = lazySchema(() =>
  z.object({
    type: z.literal("lifi_swap"),
    scope: ExecutionActionScopeSchema,
    failurePolicy: ExecutionActionFailurePolicySchema.optional(),
    args: ExecutionLifiSwapArgsSchema,
  }),
);
const RelaySwapActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "relay_swap",
    args: ExecutionRelaySwapArgsSchema,
  }),
);
const RelayBridgeActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "relay_bridge",
    args: ExecutionRelayBridgeArgsSchema,
  }),
);
const LifiBridgeActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "lifi_bridge",
    args: z.union([
      z.discriminatedUnion("quoteMode", [
        z.object({
          quoteMode: z.literal("provided"),
          fromChainId: z.number().int().positive(),
          toChainId: z.number().int().positive(),
          route: PresentObjectSchema,
          slippageBps: slippageBpsSchema,
          nonceScopeId: z.string().min(1).optional(),
          strategyInstanceId: z.string().min(1).optional(),
          stepIndex: z.number().int().nonnegative().optional(),
        }),
        z.object({
          quoteMode: z.literal("fetch"),
          fromChainId: z.number().int().positive(),
          toChainId: z.number().int().positive(),
          fromTokenAddress: AddressSchema,
          toTokenAddress: AddressSchema,
          amount: AmountSchema,
          toAddress: AddressSchema.optional(),
          slippageBps: slippageBpsSchema,
          nonceScopeId: z.string().min(1).optional(),
          strategyInstanceId: z.string().min(1).optional(),
          stepIndex: z.number().int().nonnegative().optional(),
        }),
      ]),
      z.object({
        assetId: AssetIdSchema,
        amount: AmountSchema,
        destinationChainId: z.number().int().positive(),
        destinationAssetId: AssetIdSchema,
        slippageBps: slippageBpsSchema,
      }),
    ]),
  }),
);
const Erc4626DepositActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "erc4626_deposit",
    args: z.object({
      tokenAddress: AddressSchema,
      vaultAddress: AddressSchema,
      amount: PositiveAmountSchema,
    }),
  }),
);
const Erc4626WithdrawActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "erc4626_withdraw",
    args: z.object({
      vaultAddress: AddressSchema,
      amount: PositiveAmountSchema,
      receiver: AddressSchema.optional(),
      owner: AddressSchema.optional(),
    }),
  }),
);
const Erc4626RedeemActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "erc4626_redeem",
    args: z.object({
      vaultAddress: AddressSchema,
      amount: PositiveAmountSchema,
      receiver: AddressSchema.optional(),
      owner: AddressSchema.optional(),
      useMaxBalance: z.boolean().optional(),
      expectedFullBalanceBase: PositiveAmountSchema.optional(),
    }),
  }),
);
const ApprovedVaultActionArgsSchema = lazySchema(() =>
  z.object({
    vaultId: z.string().min(1),
    amount: AmountSchema,
  }),
);
const FluidVaultDepositActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "fluid_vault_deposit",
    args: ApprovedVaultActionArgsSchema,
  }),
);
const FluidVaultWithdrawActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "fluid_vault_withdraw",
    args: ApprovedVaultActionArgsSchema,
  }),
);
const FluidVaultRedeemActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "fluid_vault_redeem",
    args: ApprovedVaultActionArgsSchema,
  }),
);
const MorphoVaultDepositActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "morpho_vault_deposit",
    args: ApprovedVaultActionArgsSchema,
  }),
);
const MorphoVaultWithdrawActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "morpho_vault_withdraw",
    args: ApprovedVaultActionArgsSchema,
  }),
);
const MorphoVaultRedeemActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "morpho_vault_redeem",
    args: ApprovedVaultActionArgsSchema,
  }),
);
const AaveV3DepositActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "aave_v3_deposit",
    args: z.object({
      tokenAddress: AddressSchema,
      poolAddress: AddressSchema,
      amount: AmountSchema,
      onBehalfOf: AddressSchema.optional(),
    }),
  }),
);
const AaveV3WithdrawActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "aave_v3_withdraw",
    args: z.object({
      tokenAddress: AddressSchema,
      poolAddress: AddressSchema,
      amount: AmountSchema,
      recipient: AddressSchema.optional(),
    }),
  }),
);
const AaveV3GatewayDepositActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "aave_v3_gateway_deposit",
    args: z.object({
      gatewayAddress: AddressSchema,
      lendingPool: AddressSchema,
      amount: AmountSchema,
      onBehalfOf: AddressSchema.optional(),
      referralCode: z.number().int().min(0).max(65_535).optional(),
    }),
  }),
);
const AaveV3GatewayWithdrawActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "aave_v3_gateway_withdraw",
    args: z.object({
      gatewayAddress: AddressSchema,
      lendingPool: AddressSchema,
      amount: AmountSchema,
      recipient: AddressSchema.optional(),
    }),
  }),
);
const WethWrapActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "weth_wrap",
    args: z.object({
      wethAddress: AddressSchema,
      amount: AmountSchema,
    }),
  }),
);
const WethUnwrapActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "weth_unwrap",
    args: z.object({
      wethAddress: AddressSchema,
      amount: AmountSchema,
    }),
  }),
);
const ZeroExSwapActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "zeroex_swap",
    args: z.object({
      quote: z
        .object({
          sellToken: AddressSchema.optional(),
          buyToken: AddressSchema.optional(),
          sellAmount: AmountSchema.optional(),
          transaction: z.object({
            to: AddressSchema,
            data: HexSchema,
            value: z.union([z.string(), z.number(), z.bigint()]).optional(),
          }),
          issues: z
            .object({
              allowance: z
                .object({
                  spender: AddressSchema.optional(),
                })
                .optional(),
            })
            .optional(),
          meta: z
            .object({
              swapId: z.string().min(1).optional(),
            })
            .passthrough()
            .optional(),
        })
        .passthrough(),
      nonceScopeId: z.string().min(1).optional(),
      strategyInstanceId: z.string().min(1).optional(),
    }),
  }),
);
const Erc20ApproveActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "erc20_approve",
    args: z.object({
      tokenAddress: AddressSchema,
      spender: AddressSchema,
      amount: AmountSchema,
    }),
  }),
);
const EvmCallActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "evm_call",
    args: ExecutionCallArgsSchema,
  }),
);

/**
 * Generic rebalance batch action. One action == one atomic multi-call EVM
 * userOp carrying the ordered calls produced by the rebalance translator's
 * generic-evm adapter. Each call is a typed leg (approve/swap/deposit/...)
 * sized in raw token units.
 *
 * See features/rebalance-engine-v2/TECHNICAL_DESIGN.md.
 */
const RebalanceBatchActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "rebalance_batch",
    args: RebalanceBatchArgsSchema,
  }),
);

const addOndoDirectBatchScopeChainIssues = (params: {
  readonly chainId?: number;
  readonly args: ExecutionOndoDirectBatchBaseArgs;
  readonly ctx: z.RefinementCtx;
}): void => {
  const { chainId, args, ctx } = params;
  if (chainId === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Ondo Direct batch execution requires an eip155 scope",
      path: ["scope", "chainRef"],
    });
    return;
  }
  const entries = [
    { assetId: args.cashAssetId, path: ["args", "cashAssetId"] },
    ...listOndoDirectBatchLegs(args).map(({ leg, side, index }) => ({
      assetId: leg.assetId,
      path: ["args", side, index, "assetId"],
    })),
  ];
  for (const entry of entries) {
    const assetChainId = parseEip155AssetChainId(entry.assetId);
    if (assetChainId !== null && assetChainId !== chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Ondo Direct batch assetId must reference the same chain as scope.chainId",
        path: entry.path,
      });
    }
  }
};

const addRealityViaBitgetScopeChainIssues = (params: {
  readonly chainId?: number;
  readonly args: {
    readonly sellAssetId: AssetId;
    readonly buyAssetId: AssetId;
  };
  readonly ctx: z.RefinementCtx;
}): void => {
  const { chainId, args, ctx } = params;
  if (chainId !== 42161) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Reality via Bitget execution requires an eip155:42161 scope",
      path: ["scope", "chainRef"],
    });
  }

  const assetEntries: Array<{
    readonly assetId: AssetId;
    readonly path: (string | number)[];
  }> = [
    { assetId: args.sellAssetId, path: ["args", "sellAssetId"] },
    { assetId: args.buyAssetId, path: ["args", "buyAssetId"] },
  ];
  for (const entry of assetEntries) {
    if (!isEip155Erc20AssetOnChain(entry.assetId, 42161)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Reality via Bitget assetId must be an ERC-20 on eip155:42161",
        path: entry.path,
      });
    }
  }
};

// Concentrated-liquidity family (dual-investment accumulator). Ticks arrive concrete
// from the automation layer; MIN/MAX bound per v3 TickMath domain.
const TickSchema = lazySchema(() => z.number().int().min(-887272).max(887272));
const ClPositionTokenIdSchema = lazySchema(() =>
  z.string().regex(/^\d+$/, "tokenId must be a decimal string"),
);
const ClDeadlineSchema = lazySchema(() => z.string().regex(/^\d+$/).optional());
// NOTE: the "liquidity" FAMILY alias is intentionally absent from the execute
// union — with five cl_* candidates it can never disambiguate (every request
// would be ACTION_ALIAS_AMBIGUOUS), so explicit cl_* types are the only
// accepted inputs until a verb discriminator exists.
const ClMintActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "cl_mint",
    args: z
      .object({
        poolId: z.string().min(1),
        tickLower: TickSchema,
        tickUpper: TickSchema,
        quoteAmountRaw: PositiveAmountSchema,
        deadline: ClDeadlineSchema,
      })
      .refine((a) => a.tickLower < a.tickUpper, {
        message: "tickLower must be below tickUpper",
      }),
  }),
);
const ClIncreaseActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "cl_increase",
    args: z.object({
      poolId: z.string().min(1),
      tokenId: ClPositionTokenIdSchema,
      quoteAmountRaw: PositiveAmountSchema,
      deadline: ClDeadlineSchema,
    }),
  }),
);
const ClDecreaseActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "cl_decrease",
    args: z
      .object({
        poolId: z.string().min(1),
        tokenId: ClPositionTokenIdSchema,
        liquidity: PositiveAmountSchema,
        amount0MinRaw: AmountSchema,
        amount1MinRaw: AmountSchema,
        deadline: ClDeadlineSchema,
        burnAfterCollect: z.boolean().optional(),
        transferLegs: z
          .tuple([
            z
              .object({ tokenAddress: AddressSchema, to: AddressSchema })
              .strict(),
            z
              .object({ tokenAddress: AddressSchema, to: AddressSchema })
              .strict(),
          ])
          .optional(),
      })
      .superRefine((args, ctx) => {
        if (args.transferLegs && args.burnAfterCollect !== true) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "delivery transfer legs require burnAfterCollect",
            path: ["burnAfterCollect"],
          });
        }
        if (
          args.transferLegs &&
          args.transferLegs[0].to.toLowerCase() !==
            args.transferLegs[1].to.toLowerCase()
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "delivery transfer legs require one shared recipient",
            path: ["transferLegs", 1, "to"],
          });
        }
        if (
          args.transferLegs?.some(
            (leg) => leg.to.toLowerCase() === ZERO_ADDRESS,
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "delivery transfer-leg recipient must not be zero",
            path: ["transferLegs"],
          });
        }
      }),
  }),
);
const ClCollectActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "cl_collect",
    args: z.object({
      poolId: z.string().min(1),
      tokenId: ClPositionTokenIdSchema,
    }),
  }),
);
const ClBurnActionInputSchema = lazySchema(() =>
  createExplicitActionInputSchema({
    type: "cl_burn",
    args: z.object({
      poolId: z.string().min(1),
      tokenId: ClPositionTokenIdSchema,
    }),
  }),
);

const ExecuteActionInputUnionSchema = lazySchema(() =>
  z.discriminatedUnion("type", [
    TransferActionInputSchema,
    YieldWithdrawActionInputSchema,
    YieldRedeemActionInputSchema,
    SwapActionInputSchema,
    BridgeActionInputSchema,
    YieldDepositActionInputSchema,
    CallActionInputSchema,
    Erc20TransferActionInputSchema,
    OwnerWithdrawalBatchActionInputSchema,
    ExternalWithdrawalBatchActionInputSchema,
    FloatTransferActionInputSchema,
    SplTransferActionInputSchema,
    SolanaDeliverProceedsActionInputSchema,
    SolanaSwapActionInputSchema,
    SolanaOndoSwapActionInputSchema,
    SolanaBridgeActionInputSchema,
    DeliverProceedsActionInputSchema,
    NativeTransferActionInputSchema,
    BebopRfqSwapActionInputSchema,
    BebopJamSwapActionInputSchema,
    OndoDirectMintRedeemActionInputSchema,
    OndoDirectBatchRebalanceActionInputSchema,
    RealityViaBitgetSwapActionInputSchema,
    LifiSwapActionInputSchema,
    LifiBridgeActionInputSchema,
    RelaySwapActionInputSchema,
    RelayBridgeActionInputSchema,
    Erc4626DepositActionInputSchema,
    Erc4626WithdrawActionInputSchema,
    Erc4626RedeemActionInputSchema,
    FluidVaultDepositActionInputSchema,
    FluidVaultWithdrawActionInputSchema,
    FluidVaultRedeemActionInputSchema,
    MorphoVaultDepositActionInputSchema,
    MorphoVaultWithdrawActionInputSchema,
    MorphoVaultRedeemActionInputSchema,
    AaveV3DepositActionInputSchema,
    AaveV3WithdrawActionInputSchema,
    AaveV3GatewayDepositActionInputSchema,
    AaveV3GatewayWithdrawActionInputSchema,
    WethWrapActionInputSchema,
    WethUnwrapActionInputSchema,
    ZeroExSwapActionInputSchema,
    Erc20ApproveActionInputSchema,
    EvmCallActionInputSchema,
    RebalanceBatchActionInputSchema,
    ClMintActionInputSchema,
    ClIncreaseActionInputSchema,
    ClDecreaseActionInputSchema,
    ClCollectActionInputSchema,
    ClBurnActionInputSchema,
  ]),
);

export const ExecuteActionInputSchema = lazySchema(() =>
  z
    .unknown()
    .superRefine((input, ctx) => {
      if (
        isPlainRecord(input) &&
        input.type !== "relay_swap" &&
        input.type !== "relay_bridge" &&
        isPlainRecord(input.args) &&
        hasDefinedKey(input.args, "appFee")
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "app fees require an explicit supported Relay action",
          path: ["args", "appFee"],
        });
      }
    })
    .pipe(ExecuteActionInputUnionSchema)
    .superRefine((action, ctx) => {
      if (
        action.type === "owner_withdrawal_batch" ||
        action.type === "external_withdrawal_batch"
      ) {
        if (
          !action.args.executionAccountRef.startsWith(
            `${action.scope.chainRef}:`,
          )
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["args", "executionAccountRef"],
            message: "Execution account must match the action chain",
          });
        }
        if (
          action.failurePolicy !== undefined &&
          action.failurePolicy !== "fail_fast"
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["failurePolicy"],
            message: "Withdrawal batches require fail_fast",
          });
        }
        return;
      }
      if (
        action.type === "spl_transfer" ||
        action.type === "solana_deliver_proceeds"
      ) {
        const asset = Parser.parseAssetId(action.args.assetId);
        const assetChainRef = `${asset.chain.namespace}:${asset.chain.reference}`;
        const isSplToken =
          asset.asset.namespace === "spl" &&
          SolanaAddressSchema.safeParse(asset.asset.reference).success;
        const isNativeSol =
          asset.asset.namespace === "slip44" &&
          asset.asset.reference === SOLANA_NATIVE_SLIP44_REFERENCE;
        if (action.type === "solana_deliver_proceeds") {
          const account = action.args.executionAccountRef;
          const address = account.slice(account.lastIndexOf(":") + 1);
          if (
            account.slice(0, account.lastIndexOf(":")) !==
              action.scope.chainRef ||
            !SolanaAddressSchema.safeParse(address).success ||
            action.failurePolicy !== "fail_fast"
          )
            ctx.addIssue({
              code: "custom",
              message:
                "Proceeds delivery requires a matching source account and fail_fast",
            });
        }
        if (
          asset.chain.namespace !== "solana" ||
          assetChainRef !== action.scope.chainRef ||
          (!isSplToken &&
            (action.type === "solana_deliver_proceeds" || !isNativeSol))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "spl_transfer assetId must be a Solana SPL asset or native SOL on scope.chainRef",
            path: ["args", "assetId"],
          });
        }
        return;
      }
      if (action.type === "solana_swap" || action.type === "solana_ondo_swap") {
        addSolanaSwapScopeIssues(action, ctx);
        return;
      }
      if (action.type === "solana_bridge") {
        addSolanaBridgeScopeIssues(action, ctx);
        return;
      }
      if (action.type === "ondo_direct_batch_rebalance") {
        addOndoDirectBatchScopeChainIssues({
          chainId: action.scope.chainId,
          args: action.args,
          ctx,
        });
        return;
      }
      if (action.type === "reality_via_bitget_swap") {
        addRealityViaBitgetScopeChainIssues({
          chainId: action.scope.chainId,
          args: action.args,
          ctx,
        });
        return;
      }
      if (action.type === ACTION_FAMILY.swap && "cashAssetId" in action.args) {
        addOndoDirectBatchScopeChainIssues({
          chainId: action.scope.chainId,
          args: action.args,
          ctx,
        });
      }
    }),
);
